import { Suspense, lazy, useEffect, useMemo, useRef, useState } from "react";
import { Card, Inline, Stack, useTheme } from "@genius-tracker/design-system";
import { useLocale } from "@genius-tracker/i18n/react";
import type { Locale } from "@genius-tracker/i18n";
import { OverviewPage } from "./overview/OverviewPage.js";
// GT-GFI-OWNER-SMOKE-FIX-RISK-RADAR-VISIBILITY-01 — dedicated, discoverable
// Admin AI Copilot & Risk Radar + Delivery Health page. Platform-owner only;
// fixes the Owner-smoke failure where the Risk Radar (an Overview-only card)
// and Delivery Health (API-only, no UI) had no nav entry.
const AdminAiCopilotPage = lazy(() =>
  import("./ai-copilot/AdminAiCopilotPage.js").then((m) => ({ default: m.AdminAiCopilotPage })),
);
// GT-GFI-AI-PROVIDER-COMPARISON-SMOKE-WORKBENCH-01 (DL-112) — Platform-Owner
// Provider Comparison & Smoke Workbench. Sibling of the AI Copilot page in
// the AI section; owner-only (nav-filtered + content-gated). Dry-run preview
// only — no provider call.
const AdminAiWorkbenchPage = lazy(() =>
  import("./ai-workbench/AdminAiWorkbenchPage.js").then((m) => ({
    default: m.AdminAiWorkbenchPage,
  })),
);
// GT-GI-UNANSWERED-QUESTIONS-LOGGING-AND-ADMIN-COVERAGE-PR-B-01 (DL-138) —
// Platform-Owner GI Coverage Gaps page (owner-gated; joins the AI section).
const AdminGiCoveragePage = lazy(() =>
  import("./gi-coverage/AdminGiCoveragePage.js").then((m) => ({ default: m.AdminGiCoveragePage })),
);
// Sprint 54.2 — Members & Invitations IA dedup. The two pages are
// now rendered inside a single `<PeoplePage />` container under one
// nav entry ("People"). The standalone components are still imported
// indirectly via PeoplePage so their tests + behaviours stay intact.
const PeoplePage = lazy(() =>
  import("./people/PeoplePage.js").then((m) => ({ default: m.PeoplePage })),
);
const BrandingPage = lazy(() =>
  import("./branding/BrandingPage.js").then((m) => ({ default: m.BrandingPage })),
);
const DiagnosticsPage = lazy(() =>
  import("./diagnostics/DiagnosticsPage.js").then((m) => ({ default: m.DiagnosticsPage })),
);
const RoleCatalogPage = lazy(() =>
  import("./access/RoleCatalogPage.js").then((m) => ({ default: m.RoleCatalogPage })),
);
const EntitlementsPage = lazy(() =>
  import("./entitlements/EntitlementsPage.js").then((m) => ({ default: m.EntitlementsPage })),
);
const AuditPage = lazy(() =>
  import("./audit/AuditPage.js").then((m) => ({ default: m.AuditPage })),
);
// Sprint 23 Wave B (S23-B3) — Platform-Owner tenant CRUD page.
// Operator decision Q11: Admin Console = Platform-Owner. The page
// itself is gated on the `platform.owner` JWT-claim role; non-
// platform-owners see a calm "platform-owner only" message
// instead of a 4xx.
const TenantsPage = lazy(() =>
  import("./tenants/TenantsPage.js").then((m) => ({ default: m.TenantsPage })),
);
// Sprint 23 Wave D (S23-D4) — Platform-Owner device approval queue.
const DeviceApprovalPage = lazy(() =>
  import("./devices/DeviceApprovalPage.js").then((m) => ({ default: m.DeviceApprovalPage })),
);
// Sprint 23 Wave E (S23-E3) — Platform-Owner SIM inventory page.
const SimsPage = lazy(() => import("./sims/SimsPage.js").then((m) => ({ default: m.SimsPage })));
// Sprint 24.1 Hotfix PR-H5 — Platform-Owner managed-hardware
// pool reader (operator finding 8: previously undiscoverable).
const ManagedHardwarePage = lazy(() =>
  import("./managed-hardware/ManagedHardwarePage.js").then((m) => ({
    default: m.ManagedHardwarePage,
  })),
);
// Sprint 24 Wave E (S24-E4) — Platform-Owner gateway rejection panel.
const GatewayRejectionsPanel = lazy(() =>
  import("./gateway/GatewayRejectionsPanel.js").then((m) => ({
    default: m.GatewayRejectionsPanel,
  })),
);
// Sprint GT-GPSDATA-INGEST-RATE-LIMIT-OBSERVABILITY-01 — Platform-Owner
// /gpsdata ingest observability panel (rate-limit status + recent events).
const GatewayIngestPanel = lazy(() =>
  import("./gateway/GatewayIngestPanel.js").then((m) => ({ default: m.GatewayIngestPanel })),
);
// Sprint 24 Wave E (S24-E5) — read-only Tenant 360° + redacted permission diagnostics.
const TenantDetailPage = lazy(() =>
  import("./tenants/TenantDetailPage.js").then((m) => ({ default: m.TenantDetailPage })),
);
// Sprint 48.10 (theme 19 — Platform Admin Diagnostics) — UI viewer
// for the Sprint 46.0 `/v1/admin/recent-errors` ring buffer. Gated
// platform.owner-only; read-only.
const RecentErrorsPage = lazy(() =>
  import("./diagnostics/RecentErrorsPage.js").then((m) => ({ default: m.RecentErrorsPage })),
);
// GT-DATA-PLANE-ADMIN-INTEGRATION-01 (Phase 5 of the Distributed
// Customer Data Plane initiative) — Database Infrastructure Health
// Center. Promoted from the hidden lab page in PR #465 to a real
// surface backed by the read-only API from PR #475. Platform-owner
// gated; read-only; no live actions.
const DataPlaneHealthPage = lazy(() =>
  import("./data-plane/DataPlaneHealthPage.js").then((m) => ({ default: m.DataPlaneHealthPage })),
);
// GT-ADMIN-DBOPS-WAVE-09 — Admin Console DB-Ops Control Plane shell.
// Read-only shell page; Overview / Inventory / Readiness UIs land
// in Waves 10–12. Platform-owner gated; behind the
// `DB_OPS_READ_UI_ENABLED` server flag (the API surface returns 404
// when the flag is off, which the page handles in later waves).
const DbOpsShellPage = lazy(() =>
  import("./db-ops/DbOpsShellPage.js").then((m) => ({ default: m.DbOpsShellPage })),
);
// GT-ADMIN-MONITORING-ALERTS-READONLY-MR-03-ADMIN-UI-01 — Data Plane
// Monitoring & Alerts page. Read-only consumer of the MR-02
// `/v1/admin/data-plane/monitoring/summary` route. Platform-owner
// gated; no mutation surface; no direct Prometheus / Alertmanager
// fetch (every browser call goes through the API).
const MonitoringAlertsPage = lazy(() =>
  import("./monitoring/MonitoringAlertsPage.js").then((m) => ({ default: m.MonitoringAlertsPage })),
);
// GT-FI-QUESTION-ANALYTICS-ADMIN-INSIGHTS-01 (issue #812) — admin FI
// question analytics view. Tenant-scoped, audit.read-gated, read-only.
const InsightsAnalyticsPage = lazy(() =>
  import("./insights-analytics/InsightsAnalyticsPage.js").then((m) => ({
    default: m.InsightsAnalyticsPage,
  })),
);
// Sprint 24 Wave F (S24-F2) — Admin Console smoke dashboard.
const SmokeDashboard = lazy(() =>
  import("./smoke/SmokeDashboard.js").then((m) => ({ default: m.SmokeDashboard })),
);
// Sprint 25 Wave B PR-B3F1 — Admin Console email delivery log.
const EmailDeliveryPage = lazy(() =>
  import("./email-delivery/EmailDeliveryPage.js").then((m) => ({ default: m.EmailDeliveryPage })),
);
// Sprint 34 (GT-COMMS-DOMAINS-FOUNDATION-01) — Mail health page +
// safe test-send drawer.
const MailHealthPage = lazy(() =>
  import("./mail/MailHealthPage.js").then((m) => ({ default: m.MailHealthPage })),
);
// Sprint 40 (GT-TENANT-DOMAIN-ROUTING-ACTIVATE-01) — Domain routing
// readiness page. Read-only; default-OFF feature flag; explicit
// "no DNS/TLS/Keycloak change" copy in the header.
const DomainRoutingPage = lazy(() =>
  import("./domain-routing/DomainRoutingPage.js").then((m) => ({ default: m.DomainRoutingPage })),
);
// Sprint 41 (GT-TELEGRAM-OPS-BOT-01) — Telegram readiness page.
// Read-only; default-OFF master flag; explicit "no real Telegram
// traffic until Owner-Go" banner in the header.
const TelegramHealthPage = lazy(() =>
  import("./telegram/TelegramHealthPage.js").then((m) => ({ default: m.TelegramHealthPage })),
);
// Sprint 55.3 — Customer Setup Wizard removed. The guided
// invite-first-admin + verify-mail flow moved inline to
// Tenant 360° (see TenantInviteAdminCard). Tenant creation was
// already on the Tenants page; the wizard duplicated both.
// Sprint 25 Wave C PR-C4 — Customer Launch Dashboard cross-tenant
// readiness pipeline.
const CustomerLaunchDashboardPage = lazy(() =>
  import("./customer-launch/CustomerLaunchDashboardPage.js").then((m) => ({
    default: m.CustomerLaunchDashboardPage,
  })),
);
// The flag client stays: `sprint26.supportInbox` still gates a real
// product page below. Only the QA-harness panels it also used to
// gate are gone.
import { useSprint26FlagVisible } from "./lib/featureFlags.js";
// Sprint 46.0 (GT-LOOP-FLEET-OPS-COMPLETE-46.0) — Live admin Support
// Inbox UI on top of the Sprint 44 + 45 backend. Replaces the
// Sprint 26 readiness placeholder; the readiness page file is kept
// on disk for historical reference but no longer rendered.
const SupportInboxPage = lazy(() =>
  import("./support/SupportInboxPage.js").then((m) => ({ default: m.SupportInboxPage })),
);
// Sprint 51.0 — Owner Purge Center (Danger Zone). Platform-owner
// only; double-confirmation + countdown + audit tombstone. See
// docs/sprints/admin-console-completion-51/PURGE_CENTER_SAFETY_MODEL.md.
const PurgeCenterPage = lazy(() =>
  import("./purge/PurgeCenterPage.js").then((m) => ({ default: m.PurgeCenterPage })),
);
// Sprint 55.3 — profile self-service page (password + email change).
const ProfilePage = lazy(() =>
  import("./profile/ProfilePage.js").then((m) => ({ default: m.ProfilePage })),
);
// GTCC-BAU G1 — GENIUSTRACKER CONTROL COCKPIT shell skeleton. Own
// internal nav/topbar/content-frame; gates itself on
// GTCC_SHELL_ENABLED + platform.owner (defence in depth, see the
// nav-entry filter below).
import { GtccShell } from "./gtcc/GtccShell.js";
// Sprint 24 Wave B (S24-B2) — Admin Console account menu + sign-out.
// Wires the existing `logout()` helper from `lib/api.ts` into a header
// popover. See `KEYCLOAK_PROVISIONING_STRATEGY.md` §2a — does NOT call
// Keycloak `sendVerifyEmail` (held in Sprint 24).
import { AccountMenu } from "./components/AccountMenu.js";
import { useSession } from "./session/SessionProvider.js";
import { usePlatformOwner } from "./access/platform-owner.js";
// A-GTCC-RED1 — where a login lands INSIDE the admin bundle. The BFF
// already lands it on the right ORIGIN; this picks the right page there.
import { resolveInitialAdminPage } from "./lib/postLoginLanding.js";
import { resolveGtccFlags } from "@genius-tracker/config";
// Sprint 24 Wave D (S24-D1) — responsive shell. The Sprint 23 staging
// smoke flagged the Admin Console as unusable on iPhone (horizontal
// scroll, nav unreachable). Below the mobile breakpoint the inline
// nav is replaced by a hamburger button + off-canvas drawer that
// reuses the SAME nav links (no duplicate routing, no duplicate
// shell). Single-shell invariant preserved.
import { useIsMobile } from "./components/useIsMobile.js";
import { MobileNavDrawer } from "./components/MobileNavDrawer.js";
// Sprint 24.1 PR-H4 — minimal cross-page navigation context.
// Sprint 25 Wave C PR-C4 — extended with `params` so the Customer
// Launch Dashboard can drill into a specific tenant on Tenant 360°.
import { AdminNavProvider, type AdminNavParams } from "./lib/adminNav.js";
// A-GTCC-LAZY2-ADMIN — bleibt bewusst ein STATISCHER Import: die Seite wird
// zusaetzlich von `gtcc/views/perm/GtccPermissionsView.tsx` statisch importiert.
// Ein `React.lazy` hier wuerde das Modul NICHT aus dem index-Chunk loesen (Rollup
// warnt genau darauf) und nur eine irrefuehrende Suspense-Grenze erzeugen.
import { PermissionsDiagnosticsPage } from "./diagnostics/PermissionsDiagnosticsPage.js";

/**
 * Admin console shell.
 *
 * Internal-only operator console. Same design system and i18n as the
 * customer-facing web app, but a different tenant-roles default and
 * no maps SDK (the admin surface never renders a map).
 *
 * Routing is a lightweight state switch rather than react-router —
 * the admin surface is 2 pages for Sprint 6, and pulling in another
 * router is net-negative for bundle size. We'll promote it once the
 * route count justifies it.
 */
/**
 * Sprint 31 (GT-TENANT-ADMIN-01) — Platform Admin IA cleanup.
 *
 * The console's IA is restructured around the brief's target groups:
 *   Overview · Tenants · Provisioning · Hardware & SIMs · Gateway ·
 *   Diagnostics · Mail · Audit · Platform Settings.
 *
 * Section labels are rendered above each group on desktop and as
 * headings in the mobile drawer so an operator scanning the nav can
 * jump straight to the section instead of scanning 16 buttons in a
 * single row.
 *
 * No working route is removed; pages just re-group into clearer
 * sections. Realm-scoped admin pages (members / invitations / roles
 * / branding) live under "Platform Settings" because they manage the
 * shared Keycloak realm — not a single tenant. Per ADMIN_IA.md the
 * customer SPA's Admin Hub handles the tenant-scoped equivalents.
 *
 * Sprint 26-29 QA-harness panels and Support inbox stay under
 * Platform Settings; their existing flag gates filter them out when
 * the `sprint26.qaHarness` / `sprint27.qaHarness` / etc. flags are
 * off.
 */
interface AdminNavGroup {
  readonly labelKey:
    | "admin.nav.section.overview"
    // GTCC-BAU G1 — GENIUSTRACKER CONTROL COCKPIT entry point. Single
    // page ("gtcc") that renders its own self-contained Mono-Deck
    // shell; platform-owner-only + GTCC_SHELL_ENABLED-gated (filtered
    // out of the nav for non-owners / flag-off in `visibleNavGroups`).
    | "admin.nav.section.gtcc"
    // GT-GFI-OWNER-SMOKE-FIX-RISK-RADAR-VISIBILITY-01 — dedicated AI Copilot
    // section (Risk Radar + Delivery Health). Platform-owner-only; filtered
    // out of the nav for non-owners in `visibleNavGroups`.
    | "admin.nav.section.ai"
    | "admin.nav.section.tenants"
    | "admin.nav.section.hardware"
    | "admin.nav.section.gateway"
    | "admin.nav.section.diagnostics"
    | "admin.nav.section.mail"
    | "admin.nav.section.audit"
    | "admin.nav.section.platform_settings"
    // Sprint 51.0 — Danger Zone. Platform-owner-only; opens the
    // Owner Purge Center. Placed last so it stays visually + IA-wise
    // separated from the routine sections above.
    | "admin.nav.section.danger_zone";
  readonly pages: ReadonlyArray<string>;
}

const ADMIN_NAV_GROUPS: ReadonlyArray<AdminNavGroup> = [
  { labelKey: "admin.nav.section.overview", pages: ["overview", "customer_launch"] },
  // GTCC-BAU G1 — Control Cockpit entry, right after Overview.
  { labelKey: "admin.nav.section.gtcc", pages: ["gtcc"] },
  // GT-GFI-OWNER-SMOKE-FIX-RISK-RADAR-VISIBILITY-01 — AI Copilot section
  // placed right after Overview so the Platform Owner finds the Risk Radar /
  // Delivery Health without scanning every section. Single page today;
  // platform-owner-only (see the `ai_copilot` owner filter in
  // `visibleNavGroups`).
  // GT-GFI-AI-PROVIDER-COMPARISON-SMOKE-WORKBENCH-01 — the Provider
  // Comparison & Smoke Workbench joins the AI section next to AI Copilot.
  // Both pages are platform-owner-only (see the `owner` filter in
  // `visibleNavGroups`).
  { labelKey: "admin.nav.section.ai", pages: ["ai_copilot", "ai_workbench", "ai_gi_coverage"] },
  // Sprint 40 — add "domain_routing" alongside the tenant nav so the
  // platform owner can see the readiness state next to tenant slugs.
  // Sprint 55.3 — `Provisioning` section removed entirely. The
  // Customer Setup Wizard duplicated tenant creation (lives on the
  // Tenants page) and tenant selection (already implicit in the
  // Tenant 360° URL). The unique value it provided — invite first
  // admin + confirm mail delivery for a selected tenant — moved to
  // Tenant 360° as an inline `Onboard tenant admin` card.
  { labelKey: "admin.nav.section.tenants", pages: ["tenants", "tenant_360", "domain_routing"] },
  {
    labelKey: "admin.nav.section.hardware",
    pages: ["device_approval", "sims", "managed_hardware"],
  },
  { labelKey: "admin.nav.section.gateway", pages: ["gateway_rejections", "gateway_ingest"] },
  {
    labelKey: "admin.nav.section.diagnostics",
    pages: [
      "diagnostics",
      "recent_errors",
      "data_plane",
      // GT-ADMIN-DBOPS-WAVE-09 — DB-Ops Control Plane (read-only
      // shell). Lives in the Diagnostics group as a sibling of the
      // existing DB Health surface; subsequent waves promote the
      // inventory + plan-builders behind the same group.
      "db_ops",
      // GT-ADMIN-MONITORING-ALERTS-READONLY-MR-03-ADMIN-UI-01 — Data
      // Plane Monitoring & Alerts. Sibling of DB Health / DB Ops in
      // the Diagnostics group. Platform-owner gated; read-only.
      "monitoring_alerts",
      "permissions_diagnostics",
      "smoke",
    ],
  },
  // Sprint 34 — add the Mail Health/Readiness page next to the
  // existing Email Delivery log. Both stay platform-owner gated.
  // Sprint 41 — `telegram_health` joins the Mail/Notifications nav
  // group as a sibling readiness surface (no real sends in either
  // page until Owner-Go).
  {
    labelKey: "admin.nav.section.mail",
    pages: ["mail_health", "telegram_health", "email_delivery"],
  },
  // GT-FI-QUESTION-ANALYTICS-ADMIN-INSIGHTS-01 — FI question analytics
  // sits in the Audit section as a sibling of the activity log (both are
  // audit-derived + `audit.read`-gated, tenant-scoped).
  { labelKey: "admin.nav.section.audit", pages: ["audit", "insights_analytics"] },
  {
    labelKey: "admin.nav.section.platform_settings",
    pages: [
      "entitlements",
      // Sprint 54.2 — `people` consolidates the prior `members` and
      // `invitations` nav entries. The two slugs are still routable
      // (back-compat for existing CTAs in MembersPage and the
      // CustomerSetupWizard); they all render `<PeoplePage />`. The
      // standalone slugs are deliberately absent from the nav so the
      // operator sees one entry instead of two duplicate dimensions.
      "people",
      "roles",
      "branding",
      /*
        GT-FLEET-OWNER-SMOKE-STRUCTURAL-RECOVERY-01 (CI follow-up) —
        the four QA-harness entries are removed from product
        navigation.

        The binding Owner decision is that sprint numbers, wave names,
        QA-harness surfaces and other internal implementation content
        must not appear anywhere in product UI, INCLUDING the Platform
        Admin Console and Owner-only views. Wave 1 had carved the
        `admin.*` namespace out on the reading that an operator
        console is not a product surface; that carve-out was wrong and
        is retired here.

        The feature-flag clients themselves stay: they still gate real
        product behaviour elsewhere in this file.
      */
      "support_inbox",
    ],
  },
  // Sprint 51.0 — Danger Zone holds the Owner Purge Center. Kept in
  // its own section + last in the nav order so the operator never
  // navigates here by accident.
  { labelKey: "admin.nav.section.danger_zone", pages: ["purge_center"] },
];
type AdminPage =
  | "overview"
  // GTCC-BAU G1 — Control Cockpit shell (own internal nav).
  | "gtcc"
  // GT-GFI-OWNER-SMOKE-FIX-RISK-RADAR-VISIBILITY-01 — Admin AI Copilot page.
  | "ai_copilot"
  // GT-GFI-AI-PROVIDER-COMPARISON-SMOKE-WORKBENCH-01 — Provider Comparison
  // & Smoke Workbench (platform-owner-only).
  | "ai_workbench"
  // GT-GI-UNANSWERED-QUESTIONS-LOGGING-AND-ADMIN-COVERAGE-PR-B-01 — GI Coverage
  // Gaps (platform-owner-only).
  | "ai_gi_coverage"
  | "customer_launch"
  | "tenants"
  | "tenant_360"
  | "domain_routing"
  | "telegram_health"
  | "device_approval"
  | "sims"
  | "managed_hardware"
  | "gateway_rejections"
  | "gateway_ingest"
  | "diagnostics"
  | "recent_errors"
  | "data_plane"
  | "db_ops"
  | "monitoring_alerts"
  | "permissions_diagnostics"
  | "smoke"
  | "mail_health"
  | "email_delivery"
  | "audit"
  | "insights_analytics"
  | "entitlements"
  // Sprint 54.2 — consolidated People page (members + invitations).
  | "people"
  // Sprint 54.2 — `members` / `invitations` stay routable as
  // back-compat aliases; the AdminApp routes them both to PeoplePage.
  | "members"
  | "invitations"
  | "roles"
  | "branding"
  | "support_inbox"
  // Sprint 51.0 — Owner Purge Center.
  | "purge_center"
  // Sprint 55.3 — profile self-service. Routable but intentionally
  // NOT in any nav section — reached via the AccountMenu popover.
  | "profile";

export function AdminApp() {
  const { t, locale, setLocale } = useLocale();
  const { resolved, setTheme } = useTheme();
  const session = useSession();
  // GT-GFI-OWNER-SMOKE-FIX-RISK-RADAR-VISIBILITY-01 — the AI Copilot entry
  // (cross-tenant Risk Radar + Delivery Health) is platform-owner-only.
  // Hide the nav entry for non-owners (defence in depth: the page also gates
  // its content and the API 403s). When the only page in the AI section is
  // filtered out, the trailing `.filter(g => g.pages.length > 0)` drops the
  // whole section so non-owners never see it.
  const owner = usePlatformOwner(session);
  // GTCC-BAU G1 — Control Cockpit nav entry is platform-owner-only AND
  // gated on GTCC_SHELL_ENABLED; hide it from the nav (defence in depth —
  // GtccShell also gates its own content) whenever either check fails.
  const gtccShellEnabled = resolveGtccFlags({ env: import.meta.env }).GTCC_SHELL_ENABLED.enabled;
  // A-GTCC-RED1 — the cockpit-marked build of this bundle IS the Control
  // Cockpit origin (see lib/postLoginLanding.ts). Opening it on the Admin
  // Overview is why a cockpit sign-in arrived at the right URL and the
  // wrong surface. C-GTCC-LOGIN-SEP1 moved the test from the build base to
  // GT_ADMIN_SURFACE, so /srv/gtcc1 stops counting as the cockpit. Read
  // once, as the initial state: after that the operator's own navigation
  // owns `page`, so this never fights a click.
  const [page, setPage] = useState<AdminPage>(() =>
    resolveInitialAdminPage({
      env: import.meta.env,
      isPlatformOwner: owner,
      gtccShellEnabled,
    }),
  );
  // Sprint 25 Wave C PR-C4 — params channel so the launch
  // dashboard can hand a tenantId to Tenant 360° on click.
  const [navParams, setNavParams] = useState<AdminNavParams>({});
  const isMobile = useIsMobile();
  const [drawerOpen, setDrawerOpen] = useState(false);

  // Gates the live Support Inbox page. The hook returns
  // `visible: false` while the flag query is loading, so a brief
  // "off" state during boot is the safe default.
  const { visible: supportInboxVisible } = useSprint26FlagVisible("sprint26.supportInbox");
  const visibleNavGroups: ReadonlyArray<AdminNavGroup> = ADMIN_NAV_GROUPS.map((g) => ({
    labelKey: g.labelKey,
    pages: g.pages.filter((p) => {
      if (p === "gtcc" && (!owner || !gtccShellEnabled)) return false;
      if ((p === "ai_copilot" || p === "ai_workbench" || p === "ai_gi_coverage") && !owner)
        return false;
      if (p === "support_inbox" && !supportInboxVisible) return false;
      return true;
    }),
  })).filter((g) => g.pages.length > 0);

  const dir = locale === "ar" ? "rtl" : "ltr";
  useEffect(() => {
    document.documentElement.dir = dir;
    document.documentElement.lang = locale;
  }, [dir, locale]);

  // Close the drawer whenever the operator picks a page so the
  // page content is visible without an extra tap.
  const navigateTo = (target: AdminPage) => {
    setPage(target);
    setDrawerOpen(false);
  };

  // Sprint 24.1 PR-H4 — accept a `string` so consumers don't need
  // to import the closed `AdminPage` union, then validate against
  // the nav groups before flipping the page state. Unknown values
  // are dropped with a console warning so a typo is surfaced
  // without crashing.
  // Sprint 25 PR-C4 — also accept a `params` payload so the
  // launch-dashboard row click can deliver the tenantId. We
  // always reset params on a paramless navigation so a stale id
  // can't bleed across pages.
  // Sprint 55.0 — Owner UI smoke F-55-004: the Sprint 54.2 IA
  // dedup removed `members` and `invitations` from ADMIN_NAV_GROUPS
  // (consolidated into `people`). The runtime validator below
  // rejected those legacy slugs as unknown, which silently killed
  // the MembersPage "Invite member" button (calls
  // navigateTo("invitations")). Back-compat aliases are now an
  // explicit allowlist independent of the nav structure, so any
  // existing CTA across the codebase keeps working.
  const NAV_BACK_COMPAT_ALIASES: ReadonlyArray<string> = ["members", "invitations"];
  const adminNavValue = {
    navigateTo: (target: string, params?: AdminNavParams) => {
      const isInNav = ADMIN_NAV_GROUPS.some((group) => group.pages.includes(target));
      const isBackCompat = NAV_BACK_COMPAT_ALIASES.includes(target);
      if (!isInNav && !isBackCompat) {
        if (typeof console !== "undefined") {
          console.warn("admin.nav.unknown_page", target);
        }
        return;
      }
      setNavParams(params ?? {});
      navigateTo(target as AdminPage);
    },
    params: navParams,
  };

  // D-GTCC-SHELL1 — GtccShell is a self-contained shell (own
  // nav/topbar/content-frame, own <main>/<header> landmarks). Wrapping it
  // in the Admin Console's own <header>/<main> chrome nested a second
  // main/banner landmark inside the first, breaking the single-shell
  // invariant (AdminApp.test.tsx). Render it standalone instead.
  if (page === "gtcc") {
    return (
      <AdminNavProvider value={adminNavValue}>
        <GtccShell />
      </AdminNavProvider>
    );
  }

  return (
    <AdminNavProvider value={adminNavValue}>
      <main
        // Sprint 24.1 Hotfix PR-H2 — shell-level overflow guard. The
        // staging smoke surfaced that some pages (notably Gateway /
        // Rejections, with its 4-column rejection table) pushed the
        // admin shell wider than the viewport on mobile, leaving the
        // header / nav clipped off-screen. `overflow-x: clip` prevents
        // any descendant from forcing horizontal scroll on the whole
        // page; tables that need wide layout get their own scroll
        // container (Gateway panel update lands in this same PR).
        // `clip` is preferred over `hidden` because it does not create
        // a new scroll container of its own; the older `hidden`
        // fallback is included via the inline style for browsers that
        // don't support `clip`.
        data-testid="admin-shell-main"
        style={{
          minBlockSize: "100dvh",
          background: "var(--gt-sys-bg-subtle)",
          color: "var(--gt-sys-text)",
          overflowX: "clip",
        }}
      >
        <header
          style={{
            background: "var(--gt-sys-surface)",
            borderBlockEnd: "1px solid var(--gt-sys-border)",
            paddingBlock: "var(--gt-sys-space-3)",
            paddingInline: "var(--gt-sys-space-6)",
            position: "sticky",
            insetBlockStart: 0,
            zIndex: 10,
          }}
        >
          <Inline gap={4} align="center" justify="space-between" wrap>
            <Inline gap={4} align="center">
              {isMobile ? (
                <button
                  type="button"
                  onClick={() => setDrawerOpen(true)}
                  aria-label={t("admin.nav.open_menu")}
                  aria-expanded={drawerOpen}
                  aria-controls="admin-mobile-nav-drawer"
                  data-testid="admin-mobile-nav-trigger"
                  style={hamburgerButtonStyle}
                >
                  {/* Three-line hamburger glyph; aria-label provides the
                    real accessible name. Pure CSS to avoid loading an
                    icon set just for the drawer trigger. */}
                  <span aria-hidden="true">☰</span>
                </button>
              ) : null}
              <Stack gap={0}>
                <strong style={{ fontSize: "var(--gt-sys-font-size-xl)" }}>
                  {t("admin.app.title")}
                </strong>
                <span
                  style={{
                    fontSize: "var(--gt-sys-font-size-xs)",
                    color: "var(--gt-sys-text-muted)",
                  }}
                >
                  {t("admin.app.subtitle")}
                </span>
              </Stack>
              {!isMobile ? (
                // Sprint 25 Wave A — PR-A4: launch-blocker fix.
                // The Sprint 24 nav grew to 16 entries across 5
                // groups. On narrow-laptop widths (1024-1280 px,
                // which is the default size for many operators) a
                // single-line nav pushed the right-most groups
                // (Diagnose / Audit / Smoke) off the visible width;
                // the shell-level `overflow-x: clip` then made
                // those buttons unreachable, not just visually but
                // functionally. The Inline `wrap` lets the nav
                // wrap to a second row instead of being clipped
                // out of the viewport. Mobile (< 768 px) keeps
                // using the off-canvas drawer (PR-D1).
                <nav data-testid="admin-desktop-nav" aria-label={t("admin.nav.drawer_label")}>
                  {/*
                    Sprint 47.0 — Owner finding: desktop nav was a
                    flat wall of ~25 buttons split across 9 always-
                    expanded section headers. Replace with collapsed
                    section heads that open a popover on click. The
                    section containing the active page is rendered
                    expanded by default so the operator's current
                    location is always one click away. Click outside
                    or ESC closes the popover.
                  */}
                  <CollapsibleAdminNav
                    groups={visibleNavGroups}
                    activePage={page}
                    onNavigate={(p) => navigateTo(p as AdminPage)}
                    t={t}
                  />
                </nav>
              ) : null}
            </Inline>
            <Inline gap={2} align="center">
              <LocaleSwitcher current={locale} onChange={setLocale} />
              <ThemeToggle
                resolved={resolved}
                onToggle={() => setTheme(resolved === "dark" ? "light" : "dark")}
              />
              <AccountMenu session={session} onProfileClick={() => navigateTo("profile")} />
            </Inline>
          </Inline>
          <MobileNavDrawer
            open={isMobile && drawerOpen}
            onClose={() => setDrawerOpen(false)}
            ariaLabel={t("admin.nav.drawer_label")}
          >
            <div id="admin-mobile-nav-drawer">
              <Stack gap={3}>
                <Inline justify="space-between" align="center">
                  <strong style={{ fontSize: "var(--gt-sys-font-size-lg)" }}>
                    {t("admin.app.title")}
                  </strong>
                  <button
                    type="button"
                    onClick={() => setDrawerOpen(false)}
                    aria-label={t("admin.nav.close_menu")}
                    data-testid="admin-mobile-nav-close"
                    style={hamburgerButtonStyle}
                  >
                    <span aria-hidden="true">×</span>
                  </button>
                </Inline>
                <nav aria-label={t("admin.nav.drawer_label")}>
                  <Stack gap={2}>
                    {visibleNavGroups.map((group, groupIdx) => (
                      <Stack
                        key={group.labelKey}
                        gap={1}
                        data-testid={`admin-nav-section-${group.labelKey}`}
                      >
                        <span style={navSectionLabelStyle}>{t(group.labelKey)}</span>
                        {group.pages.map((p) => (
                          <NavButton
                            key={p}
                            active={page === p}
                            onClick={() => navigateTo(p as AdminPage)}
                            label={t(`admin.nav.${p}` as const)}
                          />
                        ))}
                        {groupIdx < visibleNavGroups.length - 1 ? (
                          <hr
                            style={{
                              border: "none",
                              borderBlockEnd: "1px solid var(--gt-sys-border)",
                              marginBlock: 4,
                            }}
                          />
                        ) : null}
                      </Stack>
                    ))}
                  </Stack>
                </nav>
              </Stack>
            </div>
          </MobileNavDrawer>
        </header>

        <section
          data-testid="admin-shell-content"
          style={{
            maxInlineSize: 1200,
            marginInline: "auto",
            // Sprint 24.1 PR-H2 — `min-inline-size: 0` lets flex/grid
            // descendants (e.g. the rejection table inside Gateway
            // panel) shrink inside the section instead of pushing it
            // wider than the viewport. The `<main>` `overflow-x: clip`
            // is the outer guarantee; this is the inner cooperation.
            minInlineSize: 0,
            paddingInline: "var(--gt-sys-space-6)",
            paddingBlock: "var(--gt-sys-space-6)",
          }}
        >
          <Suspense fallback={<AdminPageFallback label={t("common.loading")} />}>
            {page === "overview" ? (
              <OverviewPage />
            ) : page === "ai_copilot" ? (
              // GT-GFI-OWNER-SMOKE-FIX-RISK-RADAR-VISIBILITY-01 — Admin AI
              // Copilot & Risk Radar + Delivery Health. The page itself
              // re-gates on platform.owner (calm gate for a non-owner who
              // reaches it via a stale link).
              <AdminAiCopilotPage />
            ) : page === "ai_workbench" ? (
              // GT-GFI-AI-PROVIDER-COMPARISON-SMOKE-WORKBENCH-01 — Provider
              // Comparison & Smoke Workbench. Re-gates on platform.owner (calm
              // gate for a non-owner who reaches it via a stale link).
              <AdminAiWorkbenchPage />
            ) : page === "ai_gi_coverage" ? (
              // GT-GI-UNANSWERED-QUESTIONS-LOGGING-AND-ADMIN-COVERAGE-PR-B-01 — GI
              // Coverage Gaps. Re-gates on platform.owner (calm gate for a
              // non-owner who reaches it via a stale link); the API also 403s.
              <AdminGiCoveragePage />
            ) : page === "people" ? (
              <PeoplePage />
            ) : page === "members" ? (
              // Sprint 54.2 — `members` alias routes to PeoplePage with
              // auto-scroll to the members section so any existing CTA
              // that navigates to "members" keeps working.
              <PeoplePage initialSection="members" />
            ) : page === "invitations" ? (
              // Sprint 54.2 — `invitations` alias routes to PeoplePage
              // with auto-scroll to the invitations section. Preserves
              // the MembersPage Invite-member CTA + the
              // CustomerSetupWizard review-step deep link.
              <PeoplePage initialSection="invitations" />
            ) : page === "branding" ? (
              <BrandingPage />
            ) : page === "recent_errors" ? (
              <RecentErrorsPage />
            ) : page === "data_plane" ? (
              <DataPlaneHealthPage />
            ) : page === "db_ops" ? (
              <DbOpsShellPage />
            ) : page === "monitoring_alerts" ? (
              <MonitoringAlertsPage />
            ) : page === "diagnostics" ? (
              <DiagnosticsPage />
            ) : page === "roles" ? (
              <RoleCatalogPage />
            ) : page === "audit" ? (
              <AuditPage />
            ) : page === "insights_analytics" ? (
              <InsightsAnalyticsPage />
            ) : page === "tenants" ? (
              <TenantsPage />
            ) : page === "device_approval" ? (
              <DeviceApprovalPage />
            ) : page === "sims" ? (
              <SimsPage />
            ) : page === "managed_hardware" ? (
              <ManagedHardwarePage />
            ) : page === "gateway_rejections" ? (
              <GatewayRejectionsPanel />
            ) : page === "gateway_ingest" ? (
              <GatewayIngestPanel />
            ) : page === "tenant_360" ? (
              <TenantDetailPage />
            ) : page === "permissions_diagnostics" ? (
              <PermissionsDiagnosticsPage />
            ) : page === "smoke" ? (
              <SmokeDashboard />
            ) : page === "domain_routing" ? (
              <DomainRoutingPage />
            ) : page === "telegram_health" ? (
              <TelegramHealthPage />
            ) : page === "mail_health" ? (
              <MailHealthPage />
            ) : page === "email_delivery" ? (
              <EmailDeliveryPage />
            ) : page === "customer_launch" ? (
              <CustomerLaunchDashboardPage />
            ) : page === "support_inbox" && supportInboxVisible ? (
              // Sprint 46.0 — replace the Sprint 26 readiness placeholder
              // with the live inbox now that the Sprint 44 backend +
              // Sprint 45 close action are deployed. The readiness page
              // (`SupportInboxReadinessPage`) is kept on disk as
              // historical context but no longer rendered.
              <SupportInboxPage />
            ) : page === "support_inbox" ? (
              // Same pattern: flag-flipped-off after navigating →
              // fall through to overview.
              <OverviewPage />
            ) : page === "purge_center" ? (
              // Sprint 51.0 — Owner Purge Center.
              <PurgeCenterPage />
            ) : page === "profile" ? (
              // Sprint 55.3 — profile self-service. Reached via the
              // AccountMenu popover.
              <ProfilePage />
            ) : (
              <EntitlementsPage />
            )}
          </Suspense>
        </section>

        <footer
          style={{
            maxInlineSize: 1200,
            marginInline: "auto",
            paddingInline: "var(--gt-sys-space-6)",
            paddingBlockEnd: "var(--gt-sys-space-6)",
          }}
        >
          <Card elevation={0}>
            <span
              style={{ fontSize: "var(--gt-sys-font-size-xs)", color: "var(--gt-sys-text-muted)" }}
            >
              {t("admin.app.subtitle")}
            </span>
          </Card>
        </footer>
      </main>
    </AdminNavProvider>
  );
}

/**
 * A-GTCC-LAZY2-ADMIN — Platzhalter waehrend ein per `React.lazy` getrennter
 * gtcc1-Seiten-Chunk nachgeladen wird. Nutzt den bestehenden Katalogschluessel
 * `common.loading`; es wird KEIN neuer i18n-Schluessel eingefuehrt.
 */
function AdminPageFallback({ label }: { label: string }) {
  return (
    <div
      data-testid="admin-page-loading"
      role="status"
      aria-live="polite"
      style={{
        paddingBlock: "var(--gt-sys-space-6)",
        color: "var(--gt-sys-text-muted)",
        fontSize: "var(--gt-sys-font-size-sm)",
      }}
    >
      {label}
    </div>
  );
}

/**
 * Sprint 47.0 — collapsible desktop admin nav. Each section header
 * is a click-to-expand button; the popover holds the section's
 * pages. Only one section is open at a time. The section that
 * contains the active page is the default-open on first render so
 * the operator's location is one click away.
 */
function CollapsibleAdminNav(props: {
  groups: ReadonlyArray<AdminNavGroup>;
  activePage: string;
  onNavigate: (p: string) => void;
  t: (key: string) => string;
}) {
  // Find which section contains the active page so it's expanded by
  // default after navigation.
  const activeSectionLabel = useMemo(() => {
    for (const g of props.groups) {
      if (g.pages.includes(props.activePage)) return g.labelKey;
    }
    return null;
  }, [props.groups, props.activePage]);
  const [openSection, setOpenSection] = useState<string | null>(null);
  const navRef = useRef<HTMLDivElement | null>(null);

  // Close on ESC + click-outside.
  useEffect(() => {
    if (!openSection) return;
    const onDocClick = (e: MouseEvent) => {
      const target = e.target as Node | null;
      if (target && navRef.current && !navRef.current.contains(target)) {
        setOpenSection(null);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpenSection(null);
    };
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [openSection]);

  return (
    <div ref={navRef} style={{ position: "relative" }}>
      <Inline gap={2} align="center" wrap>
        {props.groups.map((group) => {
          const isActiveSection = group.labelKey === activeSectionLabel;
          const isOpen = openSection === group.labelKey;
          return (
            <div
              key={group.labelKey}
              data-testid={`admin-nav-section-${group.labelKey}`}
              style={{ position: "relative" }}
            >
              <button
                type="button"
                onClick={() => setOpenSection(isOpen ? null : group.labelKey)}
                aria-expanded={isOpen}
                data-testid={`admin-nav-section-toggle-${group.labelKey}`}
                style={{
                  appearance: "none",
                  background: isActiveSection
                    ? "color-mix(in oklab, var(--gt-sys-accent) 10%, transparent)"
                    : "transparent",
                  color: isActiveSection ? "var(--gt-sys-accent)" : "var(--gt-sys-text)",
                  border: "1px solid var(--gt-sys-border)",
                  paddingBlock: "var(--gt-sys-space-1)",
                  paddingInline: "var(--gt-sys-space-3)",
                  borderRadius: "var(--gt-sys-radius-sm)",
                  font: "inherit",
                  fontWeight: isActiveSection
                    ? "var(--gt-sys-font-weight-semibold)"
                    : "var(--gt-sys-font-weight-medium)",
                  cursor: "pointer",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 6,
                }}
              >
                <span>{props.t(group.labelKey)}</span>
                <span aria-hidden style={{ fontSize: 9, opacity: 0.7 }}>
                  {isOpen ? "▲" : "▼"}
                </span>
              </button>
              {isOpen ? (
                <div
                  data-testid={`admin-nav-section-popover-${group.labelKey}`}
                  style={{
                    position: "absolute",
                    insetBlockStart: "calc(100% + 4px)",
                    insetInlineStart: 0,
                    minWidth: 180,
                    background: "var(--gt-sys-surface)",
                    border: "1px solid var(--gt-sys-border)",
                    borderRadius: "var(--gt-sys-radius-md)",
                    boxShadow: "0 6px 20px rgba(0,0,0,0.12)",
                    padding: 6,
                    zIndex: 30,
                    display: "grid",
                    gap: 2,
                  }}
                >
                  {group.pages.map((p) => (
                    <NavButton
                      key={p}
                      active={props.activePage === p}
                      onClick={() => {
                        setOpenSection(null);
                        props.onNavigate(p);
                      }}
                      label={props.t(`admin.nav.${p}` as const)}
                    />
                  ))}
                </div>
              ) : null}
            </div>
          );
        })}
      </Inline>
    </div>
  );
}

function NavButton(props: { active: boolean; onClick: () => void; label: string }) {
  return (
    <button
      type="button"
      onClick={props.onClick}
      aria-current={props.active ? "page" : undefined}
      style={{
        appearance: "none",
        border: "1px solid transparent",
        background: props.active
          ? "color-mix(in oklab, var(--gt-sys-accent) 14%, transparent)"
          : "transparent",
        color: props.active ? "var(--gt-sys-accent)" : "var(--gt-sys-text)",
        paddingBlock: "var(--gt-sys-space-1)",
        paddingInline: "var(--gt-sys-space-3)",
        borderRadius: "var(--gt-sys-radius-sm)",
        font: "inherit",
        fontWeight: props.active
          ? "var(--gt-sys-font-weight-semibold)"
          : "var(--gt-sys-font-weight-medium)",
        cursor: "pointer",
      }}
    >
      {props.label}
    </button>
  );
}

function LocaleSwitcher(props: { current: Locale; onChange: (next: Locale) => void }) {
  const { t } = useLocale();
  const locales: Locale[] = ["en", "de", "fr", "ar"];
  return (
    <select
      aria-label={t("locale.switcher.label")}
      value={props.current}
      onChange={(e) => props.onChange(e.currentTarget.value as Locale)}
      style={{
        paddingBlock: "var(--gt-sys-space-1)",
        paddingInline: "var(--gt-sys-space-2)",
        borderRadius: "var(--gt-sys-radius-sm)",
        border: "1px solid var(--gt-sys-border)",
        background: "var(--gt-sys-surface)",
        color: "var(--gt-sys-text)",
        font: "inherit",
      }}
    >
      {locales.map((l) => (
        <option key={l} value={l}>
          {l.toUpperCase()}
        </option>
      ))}
    </select>
  );
}

const navSectionLabelStyle: React.CSSProperties = {
  fontSize: "var(--gt-sys-font-size-xs)",
  fontWeight: "var(--gt-sys-font-weight-semibold)",
  letterSpacing: "0.04em",
  textTransform: "uppercase",
  color: "var(--gt-sys-text-muted)",
  paddingInline: "var(--gt-sys-space-1)",
  paddingBlockEnd: 2,
};

function ThemeToggle(props: { resolved: "light" | "dark"; onToggle: () => void }) {
  const { t } = useLocale();
  return (
    <button
      type="button"
      onClick={props.onToggle}
      aria-label={t("theme.switcher.label")}
      style={{
        appearance: "none",
        border: "1px solid var(--gt-sys-border)",
        background: "var(--gt-sys-surface)",
        color: "var(--gt-sys-text)",
        paddingBlock: "var(--gt-sys-space-1)",
        paddingInline: "var(--gt-sys-space-2)",
        borderRadius: "var(--gt-sys-radius-sm)",
        font: "inherit",
        cursor: "pointer",
      }}
    >
      {props.resolved === "dark" ? "☾" : "☀"}
    </button>
  );
}

const hamburgerButtonStyle: React.CSSProperties = {
  appearance: "none",
  border: "1px solid var(--gt-sys-border)",
  background: "var(--gt-sys-surface)",
  color: "var(--gt-sys-text)",
  paddingBlock: "var(--gt-sys-space-1)",
  paddingInline: "var(--gt-sys-space-2)",
  borderRadius: "var(--gt-sys-radius-sm)",
  font: "inherit",
  fontSize: 18,
  cursor: "pointer",
  lineHeight: 1,
};
